/**
 * Assign Courses
 * Role-aware course assignment for the 5-tier hierarchy:
 *   SUPER_ADMIN / THIRD_PARTY_ADMIN — see all courses, pick any client company
 *   CERTIFICATION_BODY              — see allowed + own courses, pick their own clients
 *   CLIENT                          — see courses assigned to their company, assigns to own company users (no client picker)
 *   LEARNER                         — not routed here
 */
import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';

function AssignCourses() {
  const [courses, setCourses]           = useState([]);
  const [clients, setClients]           = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const [success, setSuccess]           = useState(null);
  const [assigning, setAssigning]       = useState(false);

  const _user         = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole      = _user.role || '';
  const userCompanyId = _user.company_id || '';

  // Role booleans
  const isSuperAdmin     = userRole === 'super_admin';
  const isThirdParty     = userRole === 'third_party_admin';
  const isCertBody       = userRole === 'certification_body';
  const isClient         = userRole === 'client';

  // Super/third-party admins assign governance-accredited courses to cert bodies.
  // Other roles assign LMS courses to company users.
  const isAdminRole = isSuperAdmin || isThirdParty;

  const coursesEndpoint = isAdminRole
    ? '/accredited-courses/'
    : isCertBody
      ? '/courses/cert-body/courses'
      : '/courses/company-courses';

  const needsClientPicker = !isClient;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [coursesRes, clientsRes] = await Promise.all([
        axiosClient.get(coursesEndpoint),
        needsClientPicker ? axiosClient.get('/cert-bodies/') : Promise.resolve({ data: [] }),
      ]);
      setCourses(coursesRes.data || []);
      setClients(clientsRes.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.userMessage || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedCourse) { alert('Please select a course'); return; }
    if (needsClientPicker && !selectedClient) { alert('Please select a certification body'); return; }

    const targetClient = isClient ? userCompanyId : selectedClient;

    setAssigning(true);
    setSuccess(null);
    setError(null);

    try {
      let successMsg;

      if (isAdminRole) {
        // Governance assignment: set provider_company_id on the accredited course
        const res = await axiosClient.patch(`/accredited-courses/${selectedCourse}`, {
          provider_company_id: parseInt(targetClient),
        });
        const cb = clients.find(c => String(c.id) === String(targetClient));
        successMsg = `"${res.data?.title || 'Course'}" assigned to ${cb?.name || 'the selected certification body'}.`;
      } else {
        // LMS assignment: assign course to all users in the company
        const res = await axiosClient.post(
          `/courses/courses/${selectedCourse}/assign/${targetClient}`
        );
        const data = res.data;
        successMsg = data.message ||
          `Course assigned to ${data.assigned_count || 0} user(s)${data.client_name ? ` in ${data.client_name}` : ''}`;
      }

      setSuccess(successMsg);
      setSelectedCourse('');
      setSelectedClient('');
      fetchData();
    } catch (err) {
      console.error('Error assigning course:', err);
      setError(err.userMessage || err.response?.data?.detail || 'Failed to assign course');
    } finally {
      setAssigning(false);
    }
  };

  // ── Subtitle per role ──────────────────────────────────────────────────────

  const subtitleMap = {
    super_admin:        'Assign any course to any client company',
    third_party_admin:  'Assign courses to your client companies',
    certification_body: 'Assign your allowed or owned courses to your clients',
    client:             'Assign company-approved courses to your organisation\'s learners',
  };

  // ── Empty-state hint per role ──────────────────────────────────────────────

  const noCoursesHint = {
    super_admin:        'Create courses in Course Manager first',
    third_party_admin:  'Create courses in Course Manager first',
    certification_body: 'Contact an administrator to get courses assigned to your certification body',
    client:             'Contact your certification body to have courses assigned to your company',
  };

  // ── Styles ────────────────────────────────────────────────────────────────

  const containerStyle = { padding: '24px', maxWidth: '800px', margin: '0 auto' };
  const titleStyle     = { fontSize: '2rem', fontWeight: '700', color: '#1a1a1a', margin: 0 };
  const cardStyle      = { background: '#fff', border: '2px solid #d4af37', borderRadius: '8px', padding: '32px', marginBottom: '24px' };
  const labelStyle     = { display: 'block', fontWeight: '600', color: '#1a1a1a', marginBottom: '8px', fontSize: '1rem' };
  const selectStyle    = { width: '100%', padding: '12px', fontSize: '1rem', border: '2px solid #d4af37', borderRadius: '4px', background: '#fff', color: '#1a1a1a', fontWeight: '500', marginBottom: '24px' };
  const buttonStyle    = { padding: '14px 32px', fontSize: '1rem', fontWeight: '600', background: '#d4af37', color: '#1a1a1a', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '100%' };
  const emptyStyle     = { padding: '48px 24px', textAlign: 'center', background: '#fff', border: '2px solid #d4af37', borderRadius: '8px', color: '#666' };

  if (loading) {
    return (
      <div style={containerStyle}>
        <h1 style={titleStyle}>Assign Courses</h1>
        <div style={{ ...emptyStyle, marginTop: '32px' }}>Loading...</div>
      </div>
    );
  }

  const noCourses = courses.length === 0;
  const noClients = needsClientPicker && clients.length === 0;

  return (
    <div style={containerStyle}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={titleStyle}>Assign Courses</h1>
        <p style={{ color: '#666', margin: '8px 0 0 0', fontSize: '1rem' }}>
          {subtitleMap[userRole] || 'Assign courses to learners'}
        </p>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#ffebee', border: '2px solid #e74c3c', borderRadius: '4px', color: '#c62828', marginBottom: '24px' }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ padding: '12px 16px', background: '#e8f5e9', border: '2px solid #4caf50', borderRadius: '4px', color: '#2e7d32', marginBottom: '24px' }}>
          {success}
        </div>
      )}

      {noCourses || noClients ? (
        <div style={emptyStyle}>
          <p style={{ fontSize: '1.1rem', fontWeight: '600' }}>
            {noCourses ? 'No courses available' : 'No client companies found'}
          </p>
          <p style={{ fontSize: '0.9rem', margin: '8px 0 0 0' }}>
            {noCourses
              ? noCoursesHint[userRole]
              : 'You need to have client companies before you can assign courses'}
          </p>
        </div>
      ) : (
        <div style={cardStyle}>
          {/* Cert body / client picker */}
          {needsClientPicker && (
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>
                {isAdminRole ? 'Select Certification Body *' : 'Select Client Company *'}
              </label>
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                style={selectStyle}
              >
                <option value="">-- Choose a {isAdminRole ? 'certification body' : 'company'} --</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name || client.company_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Course picker */}
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Select Course *</label>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              style={selectStyle}
            >
              <option value="">-- Choose a course --</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}{course.category ? ` — ${course.category}` : ''}{course.accreditation_status === 'approved' ? ' [Accredited]' : ''}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleAssign}
            disabled={assigning || !selectedCourse || (needsClientPicker && !selectedClient)}
            style={{
              ...buttonStyle,
              opacity: (assigning || !selectedCourse || (needsClientPicker && !selectedClient)) ? 0.5 : 1,
              cursor: (assigning || !selectedCourse || (needsClientPicker && !selectedClient)) ? 'not-allowed' : 'pointer',
            }}
          >
            {assigning ? 'Assigning...' : 'Assign Course'}
          </button>

          {selectedCourse && (needsClientPicker ? selectedClient : true) && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#f9f9f9', borderRadius: '4px', fontSize: '0.9rem', color: '#666' }}>
              <strong>Note:</strong>{' '}
              {isClient
                ? 'This will assign the selected course to all learners in your organisation.'
                : 'This will assign the selected course to all users in the chosen client company.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AssignCourses;
